/**
 * 题集列表页面
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  TicketIcon,
  UsersIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { quizApi } from '@/lib/api';
import { Quiz } from '@/types';
import { formatInDisplayTz } from '@/lib/time';

export default function QuizList() {
  const [deleteQuizId, setDeleteQuizId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // 获取题集列表
  const { data: quizzesData, isLoading, error } = useQuery({
    queryKey: ['quizzes'],
    queryFn: quizApi.getQuizzes,
  });

  // 删除题集
  const deleteMutation = useMutation({
    mutationFn: quizApi.deleteQuiz,
    onSuccess: () => {
      toast.success('题集删除成功');
      queryClient.invalidateQueries({ queryKey: ['quizzes'] });
      setDeleteQuizId(null);
    },
  });

  const quizzes = quizzesData?.quizzes || [];

  const getQuizStatus = (quiz: Quiz) => {
    const now = new Date();
    const startDate = new Date(quiz.start_at);
    const endDate = new Date(quiz.end_at);

    if (now < startDate) {
      return { status: '未开始', color: 'bg-gray-100 text-gray-800' };
    } else if (now > endDate) {
      return { status: '已结束', color: 'bg-red-100 text-red-800' };
    } else {
      return { status: '进行中', color: 'bg-green-100 text-green-800' };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">加载题集失败</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">题集管理</h1>
          <p className="mt-2 text-sm text-gray-600">
            管理所有题集，包括创建、编辑和删除
          </p>
        </div>
        <Link
          to="/quizzes/create"
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          创建题集
        </Link>
      </div>

      {/* Quiz list */}
      {quizzes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
            <PlusIcon className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">还没有题集</h3>
          <p className="text-gray-600 mb-4">创建您的第一个题集开始使用</p>
          <Link
            to="/quizzes/create"
            className="btn btn-primary"
          >
            创建题集
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {quizzes.map((quiz) => {
            const statusInfo = getQuizStatus(quiz);
            
            return (
              <div
                key={quiz.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Quiz info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {quiz.title}
                        </h3>
                        <span className={`
                          inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                          ${statusInfo.color}
                        `}>
                          {statusInfo.status}
                        </span>
                      </div>
                      
                      {/* Quiz description */}
                      {quiz.survey_json?.description && (
                        <p className="text-gray-600 mb-4">
                          {quiz.survey_json.description}
                        </p>
                      )}
                      
                      {/* Quiz stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <ChartBarIcon className="h-4 w-4 mr-2" />
                          {quiz.survey_json?.pages?.length || 0} 道题
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <TicketIcon className="h-4 w-4 mr-2" />
                          剩余 {quiz.remaining_codes || 0} 份
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <UsersIcon className="h-4 w-4 mr-2" />
                          {quiz.identity_mode === 'email' ? '邮箱验证' : quiz.identity_mode === 'phone' ? '手机验证' : '邮箱或手机号皆可'}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          可重试 {quiz.retry_limit} 次
                        </div>
                      </div>
                      
                      {/* Time info */}
                      <div className="text-sm text-gray-500">
                        <p>开始时间：{formatInDisplayTz(quiz.start_at, 'yyyy-MM-dd HH:mm')}</p>
                        <p>结束时间：{formatInDisplayTz(quiz.end_at, 'yyyy-MM-dd HH:mm')}</p>
                        {quiz.created_at && (
                          <p>创建时间：{formatInDisplayTz(quiz.created_at, 'yyyy-MM-dd HH:mm')}</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <Link
                        to={`/attempts?quiz_id=${quiz.id}`}
                        className="btn btn-secondary text-sm flex items-center gap-1"
                        title="查看答题记录"
                      >
                        <EyeIcon className="h-4 w-4" />
                        查看记录
                      </Link>
                      <Link
                        to={`/codes/${quiz.id}`}
                        className="btn btn-secondary text-sm flex items-center gap-1"
                        title="管理兑换码"
                      >
                        <TicketIcon className="h-4 w-4" />
                        兑换码
                      </Link>
                      <Link
                        to={`/quizzes/${quiz.id}/edit`}
                        className="btn btn-primary text-sm flex items-center gap-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                        编辑
                      </Link>
                      <button
                        onClick={() => setDeleteQuizId(quiz.id)}
                        className="btn btn-danger text-sm flex items-center gap-1"
                        title="删除题集"
                      >
                        <TrashIcon className="h-4 w-4" />
                        删除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteQuizId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">确认删除</h3>
            <p className="text-gray-600 mb-6">
              您确定要删除这个题集吗？此操作无法撤销，相关的答题记录和兑换码也会被删除。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteQuizId(null)}
                className="btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteQuizId)}
                disabled={deleteMutation.isPending}
                className="btn btn-danger"
              >
                {deleteMutation.isPending ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}